import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  memo,
} from "react";
import { useInView } from "react-intersection-observer";
import { db, doc, getDoc, collection, getDocs } from "../service/firebase";
import "../styles/Skills.css";

/* =============================
   Skeleton Loader
============================= */
const SkillsSkeleton = memo(() => (
  <div className="skills-skeleton-grid">
    {Array.from({ length: 3 }).map((_, i) => (
      <div key={i} className="skeleton-card">
        <div className="skeleton-header">
          <div className="skeleton-circle" />
          <div className="skeleton-content">
            <div className="skeleton-line title-line" />
            <div className="skeleton-line subtitle-line" />
          </div>
        </div>
        <div className="skeleton-line desc-line" />
        <div className="skeleton-line desc-line short" />
      </div>
    ))}
  </div>
));

SkillsSkeleton.displayName = "SkillsSkeleton";

/* =============================
   Experience Card
============================= */
const ExperienceCard = memo(({ entry, index }) => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
    rootMargin: "-50px 0px",
  });

  return (
    <article
      ref={ref}
      style={{ "--delay": `${index * 100}ms` }}
      className={`experience-card ${inView ? "animate-in" : ""}`}
      aria-label={`${entry.title} at ${entry.company}`}
    >
      <div className="timeline-connector">
        <div className="connector-line" />
        <div className="connector-dot" />
      </div>

      <div className="card-content-wrapper">
        <header className="card-header">
          <div className="header-main">
            <h3 className="company-name">{entry.company}</h3>
            <span className="role-badge">{entry.title}</span>
          </div>
          <time className="period-badge">{entry.period}</time>
        </header>

        {entry.description && (
          <p className="card-description">{entry.description}</p>
        )}

        {entry.responsibilities?.length > 0 && (
          <ul className="responsibilities-list">
            {entry.responsibilities.map((task, i) => (
              <li key={i}>{task}</li>
            ))}
          </ul>
        )}

        {entry.skills?.length > 0 && (
          <div className="tech-stack">
            {entry.skills.map((skill, i) => (
              <span key={i} className="tech-tag">
                {skill}
              </span>
            ))}
          </div>
        )}
      </div>
    </article>
  );
});

ExperienceCard.displayName = "ExperienceCard";

/* =============================
   Main Component
============================= */
const Skills = ({ isDarkMode }) => {
  const [skills, setSkills] = useState([]);
  const [experience, setExperience] = useState([]);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* =============================
     Fetch Data
  ============================= */
  useEffect(() => {
    let active = true;

    const fetchData = async () => {
      try {
       

        const [skillsDoc, expSnap] = await Promise.all([
          getDoc(doc(db, "Portfolio", "Skills")),
          getDocs(collection(db, "Portfolio", "Skills", "Experience")),
        ]);

        if (!active) return;

        // --- Skills Pills ---
        if (skillsDoc.exists()) {
          const data = skillsDoc.data();
          setSkills(Array.isArray(data.skill) ? data.skill : []);
        } else {
          console.warn("⚠️ Skills document not found");
        }

        // --- Experience Timeline ---
        const expList = expSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setExperience(expList);
      } catch (err) {
        console.error("🔥 Skills fetch error:", err);
        setError("Unable to load skills or experience data.");
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchData();
    return () => {
      active = false;
    };
  }, []);

  /* =============================
     Filter Logic
  ============================= */
  const filteredExperience = useMemo(() => {
    if (!selectedSkill) return experience;
    return experience.filter((item) =>
      item.skills?.some(
        (s) => s.toLowerCase() === selectedSkill.toLowerCase()
      )
    );
  }, [experience, selectedSkill]);

  const toggleSkill = useCallback((skill) => {
    setSelectedSkill((prev) => (prev === skill ? null : skill));
  }, []);

  return (
    <section
      id="skills"
      className={`skills-section ${isDarkMode ? "theme-dark" : "theme-light"}`}
    >
      <div className="container">
        {/* Header */}
        <header className="section-header">
          <h2 className="gradient-text">Technical Proficiency</h2>
          <p className="section-subtitle">
            Explore my journey by selecting a technology below.
          </p>
        </header>

        {loading ? (
          <SkillsSkeleton />
        ) : error ? (
          <div className="error-container">
            <p>{error}</p>
          </div>
        ) : (
          <>
            {/* Skill Filters */}
            <div className="filter-wrapper">
              <div className="skills-filter-track">
                {skills.map((skill) => (
                  <button
                    key={skill}
                    onClick={() => toggleSkill(skill)}
                    className={`filter-pill ${
                      selectedSkill === skill ? "active" : ""
                    }`}
                  >
                    {skill}
                  </button>
                ))}
              </div>

              {selectedSkill && (
                <button
                  className="clear-filter-btn"
                  onClick={() => setSelectedSkill(null)}
                >
                  Clear Selection
                </button>
              )}
            </div>

            {/* Timeline */}
            <div className="timeline-container">
              <h3 className="timeline-title">
                {selectedSkill
                  ? `Experience with ${selectedSkill}`
                  : "Professional Timeline"}
              </h3>

              <div className="timeline-feed">
                {filteredExperience.length > 0 ? (
                  filteredExperience.map((entry, i) => (
                    <ExperienceCard
                      key={entry.id}
                      entry={entry}
                      index={i}
                    />
                  ))
                ) : (
                  <div className="empty-state">
                    <p>No experience found for this filter.</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default Skills;
